import {
  Button,
  InputField,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { ReactElement, useState } from "react";
import { Icon, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderDeliveryLabel } from "../../../hooks/api/v2/order/useOrderDeliveryLabel";
import { downloadPDF } from "../../../utils/downloadHelpers";

type LabelType = "pdf" | "zpl" | "pdf4x6";
export const OrderShippingLabelModal = ({
  active,
  onDismiss,
  orderId,
  buyerName,
  fulfillmentType,
}: {
  active: boolean;
  onDismiss: () => void;
  orderId: number;
  buyerName: string;
  fulfillmentType: "ups" | "apc";
}): ReactElement => {
  const [labelType, setLabelType] = useState<LabelType>("pdf4x6");
  const orderDeliveryLabelMutation = useOrderDeliveryLabel(orderId);

  const printPdf = (pdf: string) => {
    let pdfWindow = window.open("");
    pdfWindow?.document.write(
      "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
        encodeURI(pdf) +
        "'></iframe>",
    );
  };
  const printZpl = (zpl: string) => {
    let printWindow = window.open();
    printWindow?.document.open("text/plain");
    printWindow?.document.write(atob(zpl));
    printWindow?.document.close();
    printWindow?.focus();
    printWindow?.print();
    printWindow?.close();
  };

  const handleSubmit = (isDownload: boolean = false) => {
    if (labelType) {
      orderDeliveryLabelMutation.mutate(
        {
          labelType,
        },
        {
          onSuccess: (res) => {
            if (res.mimeType === "application/pdf") {
              if (isDownload) {
                downloadPDF(`${orderId} - ${buyerName}.pdf`, res.data);
                onDismiss();
                return;
              }
              printPdf(res.data);
            } else {
              printZpl(res.data);
            }
            onDismiss();
          },
        },
      );
    }
  };
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={`Confirm label file format`}
      />
      <DialogContent>
        {orderDeliveryLabelMutation.isPending ? (
          <SpinnerLegacy />
        ) : (
          <>
            {orderDeliveryLabelMutation.isError && (
              <Alert
                type={"error"}
                message={
                  apiV2AssertError(orderDeliveryLabelMutation.error)?.response
                    ?.data.message ?? "An unknown error occurred"
                }
              />
            )}

            <div className={"flex flex-col gap-2"}>
              <InputField label={"File format"}>
                <InputSelect
                  required={true}
                  value={labelType}
                  onChange={(e) => setLabelType(e.target.value as LabelType)}
                >
                  <InputOption value="" disabled hidden>
                    Choose...
                  </InputOption>
                  <option value={"pdf"}>
                    PDF {fulfillmentType === "ups" ? "(A4)" : ""}
                  </option>
                  {fulfillmentType === "ups" ? (
                    <option value={"pdf4x6"}>PDF (4x6)</option>
                  ) : null}
                  <option value={"zpl"}>ZPL (Zebra Printer Language)</option>
                </InputSelect>
              </InputField>
            </div>
          </>
        )}
      </DialogContent>
      <DialogCTA>
        <div className="flex flex-row gap-2">
          <Button
            onClick={() => handleSubmit(true)}
            disabled={
              labelType === "zpl" || orderDeliveryLabelMutation.isPending
            }
          >
            <Icon icon={"DOWNLOAD"} />
            Save as
          </Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={orderDeliveryLabelMutation.isPending}
          >
            <Icon icon={"PRINTER"} />
            Print
          </Button>
        </div>
      </DialogCTA>
    </Dialog>
  );
};
